/* Zana takeover plans (design: ConsoleTakeover.dc.html, 2026-10-30).

   A plan is what Zana shows before she touches anything: each step says
   whether she will read, change or send, and where. Every plan is built here
   from the live records, and every step that writes calls a write the console
   already makes elsewhere (off the road, back on the road, the driver email,
   the bus announcement, marking alerts read). Nothing here invents a write.

   The rules the design sets, kept by the engine (Takeover.tsx):
   - nothing runs until the owner presses Approve;
   - a Send step always stops and shows the message first; it can be edited
     or skipped;
   - every change leaves a receipt, and a change that can be undone offers
     Undo for 10 minutes. A message cannot be unsent, so it says so. */
import { doc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { announceToBusParents } from '../../services/fleetContactApi';
import { notifyBusDriver } from '../../services/briefingsApi';
import type { PageKey } from '../kit';
import type { Bus, FleetAlert, FleetModel } from '../data/model';
import { backOnRoad, takeOffRoad } from '../data/busStatus';
import { notLoggingBuses, thirstPct, thirstyBuses } from '../data/urgent';

export type StepTag = 'Read' | 'Change' | 'Send';

/** What a finished step leaves behind. */
export interface Receipt {
  text: string;
  /** Reverses the change. Absent for messages: a message cannot be unsent. */
  undo?: () => Promise<unknown>;
  /** Said on the Undo button's title when undoing has a side effect. */
  undoNote?: string;
  /** Where to see it. */
  view?: { page: PageKey; search?: string };
}

export interface SendDraft {
  /** "To 32 parents · app" */
  to: string;
  body: string;
  button: string;
  /** Before → after lines shown above the message ("− KCE 999Z"). */
  diff?: { label: string; minus: string; plus: string };
  run: (body: string) => Promise<Receipt[]>;
}

export interface PlanStep {
  tag: StepTag;
  /** "Take KCE 999Z off the road" */
  t: string;
  /** The page it happens on, as the sidebar names it. */
  where: string;
  page: PageKey;
  search?: string;
  /** data-z value of the element she points at. */
  target: string;
  /** Meta line while the step runs: "Opening KCE 999Z…" */
  doing: string;
  /** Cursor tag: "reading", "choosing Off road". */
  cursor: string;
  /** The tag above the ringed element. */
  label: string;
  run?: () => Promise<Receipt[]>;
  send?: SendDraft;
}

export interface Plan {
  id: string;
  /** The job, in the owner's words: shown as the question bubble. */
  ask: string;
  steps: PlanStep[];
  /** Where Hand back lands. */
  home: { page: PageKey; search?: string };
}

export interface PlanCtx {
  f: FleetModel;
  by: { uid: string | null; email: string | null };
  /** "Faith" — signs messages the owner sends. */
  signer: string;
}

type Raw = Record<string, unknown>;
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;
const firstName = (s: string) => s.trim().split(/\s+/)[0] || '';
const busSearch = (b: Bus) => `?bus=${encodeURIComponent(b.id)}`;

/** "About 20 seconds · 2 changes · 1 message to 32 parents" */
export function planMeta(p: Plan): string {
  const changes = p.steps.filter((s) => s.tag === 'Change').length;
  const sends = p.steps.filter((s) => s.tag === 'Send');
  const secs = Math.max(10, Math.round((p.steps.length * 2.4) / 5) * 5);
  const parts = [`About ${secs} seconds`];
  if (changes) parts.push(plural(changes, 'change'));
  if (sends.length) parts.push(sends.length === 1 ? `1 message ${sends[0].send?.to.replace(/ · .*$/, '').replace(/^To /, 'to ')}` : plural(sends.length, 'message'));
  if (!changes && !sends.length) parts.push('nothing changes');
  return parts.join(' · ');
}

/* ── 1 · Take a bus off the road ─────────────────────────────────────────── */

/** The bus that takes this bus's children: on the road, not this bus, with
    the most free seats, a spare first. Null when no bus has room for all. */
export function hostFor(f: FleetModel, bus: Bus, kids: number): Bus | null {
  const free = (b: Bus) => (b.seats != null ? b.seats - (b.expected || 0) : null);
  const ok = f.buses.filter((b) => b.id !== bus.id && b.status !== 'off_road' && (free(b) ?? -1) >= kids);
  ok.sort((a, b) => Number(b.isSpare) - Number(a.isSpare) || (free(b) as number) - (free(a) as number));
  return ok[0] || null;
}

export function offRoadPlan(c: PlanCtx, bus: Bus, why: string): Plan | null {
  const { f } = c;
  if (bus.status === 'off_road') return null;
  const kids = (Array.isArray(bus.raw.children) ? (bus.raw.children as Raw[]) : []).filter((k) => k && str(k.childId));
  const host = kids.length ? hostFor(f, bus, kids.length) : null;
  if (kids.length && !host) return null; // nowhere to put the children: the owner chooses, in the bus's dialog
  const moves: Record<string, string> = {};
  kids.forEach((k) => { moves[str(k.childId)] = (host as Bus).id; });
  const steps: PlanStep[] = [];
  if (/insurance|inspection|paper|ntsa|expired/i.test(why)) {
    steps.push({
      tag: 'Read', t: `Open Maintenance for ${bus.plate}`, where: 'Maintenance', page: 'ConsoleMaintenance', target: `paper:${bus.id}`,
      doing: `Opening Maintenance for ${bus.plate}…`, cursor: 'reading', label: `Zana · ${bus.plate} on Maintenance`,
    });
  }
  steps.push({
    tag: 'Read', t: `Check ${bus.plate}: ${why}`, where: 'Buses', page: 'ConsoleBuses', target: `bus:${bus.id}`,
    doing: `Opening ${bus.plate}…`, cursor: 'reading', label: `Zana · reading: ${why}`,
  });
  if (kids.length && host) {
    const word = `${bus.name} is off the road for now. Your child rides ${host.name}${host.plate !== host.name ? ` (${host.plate})` : ''} until it is back — the Trasse app shows which.`;
    steps.push({
      tag: 'Send', t: `Tell the parents of the ${plural(kids.length, 'child', 'children')} on ${bus.plate}`, where: 'Messages', page: 'ConsoleBuses', search: busSearch(bus),
      target: `sheet:${bus.id}`, doing: 'Writing to the parents…', cursor: 'waiting', label: 'Zana · waiting for you to send the message',
      send: {
        to: `To the parents on ${bus.plate} · app`, body: word, button: 'Send to the parents',
        diff: { label: `${plural(kids.length, 'child', 'children')} · from the next run`, minus: `${bus.plate} · ${bus.name}`, plus: `${host.plate} · ${host.name}` },
        run: async (body) => {
          const r = await announceToBusParents(f.org.code, bus.id, body);
          if (!r.ok) throw new Error(r.message || 'The message did not go out.');
          return [{ text: `Parents on ${bus.plate} told${r.sent != null ? ` (${r.sent} of ${r.total ?? r.sent})` : ''}` }];
        },
      },
    });
  }
  steps.push({
    tag: 'Change', t: kids.length && host ? `Take ${bus.plate} off the road and move its ${plural(kids.length, 'child', 'children')} to ${host.plate}` : `Take ${bus.plate} off the road`,
    where: 'Buses', page: 'ConsoleBuses', search: busSearch(bus), target: `offroad:${bus.id}`,
    doing: 'Setting it to Off road…', cursor: 'choosing Off road', label: 'Zana · Off road',
    run: async () => {
      await takeOffRoad(f, bus, 'suspended', moves, c.by, { announce: false, source: 'zana' });
      return [{
        text: kids.length && host ? `${bus.plate} off the road · ${plural(kids.length, 'child', 'children')} moved to ${host.plate}` : `${bus.plate} off the road`,
        undo: () => backOnRoad(f, { ...bus, status: 'off_road' }, c.by, { source: 'zana' }),
        undoNote: kids.length ? 'Undo puts it back on the road and its children back on it. Parents are told it is back.' : undefined,
        view: { page: 'ConsoleBuses', search: busSearch(bus) },
      }];
    },
  });
  if (bus.driverId) {
    const name = firstName(bus.driverName) || 'its driver';
    steps.push({
      tag: 'Send', t: `Tell ${bus.driverName || 'its driver'}, its driver`, where: 'Messages', page: 'ConsoleBuses', search: busSearch(bus),
      target: `sheet:${bus.id}`, doing: 'Writing to the driver…', cursor: 'waiting', label: 'Zana · waiting for you to send the message',
      send: {
        to: `To ${bus.driverName || 'the driver'} · email`, button: `Send to ${name}`,
        body: `${bus.plate} is off the road from today: ${why}. Please do not drive it until the office says it is back.${c.signer ? ` — ${c.signer}` : ''}`,
        run: async (body) => {
          const r = await notifyBusDriver(f.org.code, bus.id, body);
          if (!r.ok) throw new Error(r.message || 'The email did not go out.');
          return [{ text: `${r.driverName || bus.driverName || 'The driver'} told` }];
        },
      },
    });
  }
  return { id: `offroad-${bus.id}`, ask: `Take ${bus.plate} off the road: ${why}.`, steps, home: { page: 'ConsoleBuses', search: busSearch(bus) } };
}

/* ── 2 · A bus burning more fuel than its normal ─────────────────────────── */

export function fuelCheckPlan(c: PlanCtx, bus: Bus): Plan | null {
  const { f } = c;
  if (bus.kmpl == null || bus.kmplNormal == null) return null;
  const pct = thirstPct(bus);
  const steps: PlanStep[] = [{
    tag: 'Read', t: `Fuel for ${bus.plate}, last 30 days`, where: 'Fuel', page: 'ConsoleFuel', target: `fuel:${bus.id}`,
    doing: `Reading ${bus.plate}’s fuel…`, cursor: 'reading', label: `Zana · ${bus.kmpl.toFixed(1)} km per litre against its own ${bus.kmplNormal.toFixed(1)}`,
  }, {
    tag: 'Read', t: 'Check its latest refills', where: 'Fuel', page: 'ConsoleFuel', target: 'refills',
    doing: 'Checking the refills…', cursor: 'reading', label: 'Zana · reading the refills',
  }];
  if (bus.driverId) steps.push({
    tag: 'Send', t: `Ask ${bus.driverName || 'its driver'} to take it for a workshop check`, where: 'Messages', page: 'ConsoleFuel',
    target: `fuel:${bus.id}`, doing: 'Writing to the driver…', cursor: 'waiting', label: 'Zana · waiting for you to send the message',
    send: {
      to: `To ${bus.driverName || 'the driver'} · email`, button: `Send to ${firstName(bus.driverName) || 'the driver'}`,
      body: `Please take ${bus.plate} to the workshop after your next run. Fuel use is up ${pct}% on its own normal — tyres, brakes and the air filter are worth a look.${c.signer ? ` — ${c.signer}` : ''}`,
      run: async (body) => {
        const r = await notifyBusDriver(f.org.code, bus.id, body);
        if (!r.ok) throw new Error(r.message || 'The email did not go out.');
        return [{ text: `${r.driverName || bus.driverName || 'The driver'} asked to take ${bus.plate} for a check`, view: { page: 'ConsoleFuel' } }];
      },
    },
  });
  return { id: `fuel-${bus.id}`, ask: `${bus.plate} is using ${pct}% more fuel than its normal. Look into it and tell its driver.`, steps, home: { page: 'ConsoleFuel' } };
}

/* ── 3 · Drivers who have not logged a refill ────────────────────────────── */

export function remindPlan(c: PlanCtx, buses: Bus[]): Plan | null {
  const { f } = c;
  const withDriver = buses.filter((b) => b.driverId);
  if (!withDriver.length) return null;
  const n = withDriver.length;
  return {
    id: 'remind-refuel', ask: `Remind the ${plural(n, 'driver')} who haven’t logged a refuel in 7 days.`, home: { page: 'ConsoleFuel' },
    steps: [{
      tag: 'Read', t: 'Find buses with no refill in 7 days', where: 'Fuel', page: 'ConsoleFuel', target: 'notlogging',
      doing: 'Reading the refills…', cursor: 'reading', label: `Zana · ${plural(n, 'bus', 'buses')} not logging`,
    }, {
      tag: 'Send', t: `Remind ${plural(n, 'driver')} to log their refills`, where: 'Messages', page: 'ConsoleFuel', target: 'notlogging',
      doing: 'Writing to the drivers…', cursor: 'waiting', label: 'Zana · waiting for you to send the message',
      send: {
        to: `To ${plural(n, 'driver')} · email`, button: `Send to ${plural(n, 'driver')}`,
        diff: { label: 'Drivers', minus: '', plus: withDriver.map((b) => `${firstName(b.driverName) || 'Driver'} · ${b.plate}`).join(', ') },
        body: 'Please log your last refuel in the Trasse app.',
        run: async (body) => {
          const sent = await Promise.all(withDriver.map((b) => notifyBusDriver(f.org.code, b.id, body).then((r) => (r.ok ? 1 : 0)).catch(() => 0)));
          const ok = sent.reduce<number>((s, x) => s + x, 0);
          if (!ok) throw new Error('None of the reminders went out.');
          return [{ text: `Reminder sent to ${plural(ok, 'driver')}${ok < n ? ` (${n - ok} didn’t go out)` : ''}` }];
        },
      },
    }],
  };
}

/* ── 4 · Open alerts ─────────────────────────────────────────────────────── */

async function setRead(ids: string[], read: boolean) {
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids.slice(i, i + 400).forEach((id) => batch.update(doc(db, 'fleetAlerts', id), { read }));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
}

export function alertsPlan(c: PlanCtx, alerts: FleetAlert[]): Plan | null {
  // Incident alerts are a driver's report: closed in Analytics, never in bulk.
  const open = alerts.filter((a) => !a.read && a.kind !== 'incident');
  if (!open.length) return null;
  const ids = open.map((a) => a.id);
  const n = open.length;
  return {
    id: 'alerts-read', ask: `Go through the ${plural(n, 'open alert')} and mark them read.`, home: { page: 'ConsoleAlerts' },
    steps: [{
      tag: 'Read', t: `Read the ${plural(n, 'open alert')}`, where: 'Alerts', page: 'ConsoleAlerts', target: 'alerts',
      doing: 'Reading the alerts…', cursor: 'reading', label: `Zana · reading ${plural(n, 'alert')}`,
    }, {
      tag: 'Change', t: `Mark ${plural(n, 'alert')} as read`, where: 'Alerts', page: 'ConsoleAlerts', target: 'alerts',
      doing: 'Marking them read…', cursor: 'marking read', label: 'Zana · marking read',
      run: async () => {
        await setRead(ids, true);
        return [{ text: `${plural(n, 'alert')} marked read`, undo: () => setRead(ids, false), view: { page: 'ConsoleAlerts' } }];
      },
    }],
  };
}

/* ── what Zana can take over right now ───────────────────────────────────── */

/** Expired insurance or inspection, in words: "insurance expired 2 days ago". */
export function papersWhy(bus: Bus, now = Date.now()): string | null {
  const expired = bus.papers.filter((p) => p.kind !== 'service' && p.due && p.due.getTime() < now);
  if (!expired.length) return null;
  const days = Math.max(1, Math.round((now - Math.max(...expired.map((p) => (p.due as Date).getTime()))) / 86400000));
  // "Insurance" → "insurance", but "NTSA inspection" keeps its capitals.
  const word = (s: string) => s.replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase());
  return `${expired.map((p) => word(p.label)).join(' and ')} expired ${days === 1 ? 'yesterday' : `${days} days ago`}`;
}

export function availablePlans(c: PlanCtx): Plan[] {
  const { f } = c;
  const out: Plan[] = [];
  f.buses.forEach((b) => { const why = papersWhy(b); const p = why ? offRoadPlan(c, b, why) : null; if (p) out.push(p); });
  const thirsty = thirstyBuses(f)[0];
  if (thirsty) { const p = fuelCheckPlan(c, thirsty); if (p) out.push(p); }
  const quiet = remindPlan(c, notLoggingBuses(f)); if (quiet) out.push(quiet);
  const al = alertsPlan(c, f.alerts); if (al) out.push(al);
  return out;
}

/** A typed job ("take KCE 999Z off the road") → its plan, when Zana has one. */
export function planForText(c: PlanCtx, q: string): Plan | null {
  const t = q.toLowerCase();
  // A question is answered in the chat. It is not a job she takes over.
  if (/\b(why|explain|what does|how come)\b/.test(t)) return null;
  const key = (s: string) => s.toLowerCase().replace(/[\s-]/g, '');
  const bus = c.f.buses.find((b) => t.replace(/[\s-]/g, '').includes(key(b.plate)) || (b.name.length > 3 && t.includes(b.name.toLowerCase())));
  if (/off the road|off road|suspend|park/.test(t) && bus) return offRoadPlan(c, bus, papersWhy(bus) || 'taken off by the office');
  if (/fuel|workshop/.test(t) && /(do it|tell|ask|check|send)/.test(t) && bus) return fuelCheckPlan(c, bus);
  if (/remind/.test(t) && /(refuel|refill|fuel|log)/.test(t)) return remindPlan(c, notLoggingBuses(c.f));
  if (/(mark|clear).*(alert)|alerts?.*(read|clear)/.test(t)) return alertsPlan(c, c.f.alerts);
  return null;
}

/* ── the practice run for the takeover tour: nothing is saved ────────────── */

export function practicePlan(real: Plan): Plan {
  const fake = (s: PlanStep): PlanStep => ({
    ...s,
    run: s.run ? async () => [{ text: `Practice: ${s.t}` }] : undefined,
    send: s.send ? { ...s.send, run: async () => [{ text: `Practice: ${s.t}` }] } : undefined,
  });
  // The tour watches her read and change, then ends on "I always ask before
  // I send": up to three steps that don't send (a job can open another
  // screen before it reads the bus), then one that does.
  const quiet = real.steps.filter((s) => !s.send).slice(0, 3).map(fake);
  const send = real.steps.find((s) => !!s.send);
  return { ...real, id: `practice-${real.id}`, steps: send ? [...quiet, fake(send)] : quiet };
}
